import { useEffect, useRef, useState } from 'react';
import { Countdown } from '../../types';
import { NavRoute } from './types';
import { DASHBOARD_HERO_PARTICLES } from '../../dashboardHomeConstants';
import { getCurrentMonthYear } from '../../../../helpers/utils/utils';
import { animateHeroTicketCount } from '../../../PrizeDraw/utils/prizeDrawHeroUtils';

const pad2 = (n: number) => String(Math.max(0, n)).padStart(2, '0');

const PayRentSparkleIcon = () => (
  <svg width='14' height='14' viewBox='0 0 24 24' fill='none' aria-hidden='true'>
    <path
      d='M12 2l1.8 5.4L19 9.2l-5.2 1.8L12 16.4l-1.8-5.4L5 9.2l5.2-1.8L12 2z'
      fill='currentColor'
    />
    <path
      d='M19 14l.9 2.7 2.7.9-2.7.9-.9 2.7-.9-2.7-2.7-.9 2.7-.9.9-2.7z'
      fill='currentColor'
      opacity='0.85'
    />
  </svg>
);

const COUNTDOWN_PARTS: Array<{ key: keyof Countdown; label: string; pad: boolean }> = [
  { key: 'days', label: 'days', pad: false },
  { key: 'hours', label: 'hrs', pad: true },
  { key: 'minutes', label: 'min', pad: true },
  { key: 'seconds', label: 'sec', pad: true },
];

const numSkeleton = (className: string) => (
  <span
    className={`inline-block rounded-md align-middle animate-pulse bg-white/25 ${className}`}
    aria-hidden
  />
);

const formatCountdownValue = (value: number, pad: boolean) =>
  pad ? pad2(value) : String(Math.max(0, value));

export const ContestHero = ({
  tickets,
  ticketsPending,
  countdown,
  countdownPending,
  onNav,
}: {
  tickets: number;
  ticketsPending: boolean;
  countdown: Countdown;
  countdownPending: boolean;
  onNav: (route: NavRoute) => void;
}) => {
  const ticketsRef = useRef<HTMLDivElement>(null);
  const displayedTicketsRef = useRef(0);
  const [displayedTickets, setDisplayedTickets] = useState(0);

  useEffect(() => {
    if (ticketsPending) return undefined;
    const el = ticketsRef.current;
    if (!el) return undefined;

    const from = displayedTicketsRef.current;
    return animateHeroTicketCount(el, from, tickets, (value) => {
      displayedTicketsRef.current = value;
      setDisplayedTickets(value);
    });
  }, [tickets, ticketsPending]);

  return (
    <div className='hero'>
      {DASHBOARD_HERO_PARTICLES.map((particle) => (
        <div
          key={`${particle.top}-${particle.left}`}
          className={`sv-particle ${particle.tone}`}
          style={{
            top: particle.top,
            left: particle.left,
            width: particle.width,
            height: particle.height,
            animationDelay: particle.delay,
            animationDuration: particle.duration,
          }}
          aria-hidden='true'
        />
      ))}
      <div className='hero-shimmer-edge' aria-hidden='true' />

      <div className='hero-body mt-[28px] mb-[28px]'>
        <div className='hero-l'>
          <div className='hero-meta'>
            <div className='badge badge-live'>
              <div className='bdot' />
              <span className='badge-live-txt'>Live</span>
            </div>
            <span className='hero-meta-divider' aria-hidden='true' />
            <div className='badge badge-neutral'>{getCurrentMonthYear()}</div>
          </div>
          <h1 className='hero-h1'>
            <span className='hero-h1-gold'>THIS MONTH&apos;S DROP IS LIVE.</span>
            <br />
            <strong>YOUR RENT COULD BE ON US.</strong>
          </h1>
          <p className='hero-p mb-[20px]'>
            Every month, we share a portion of our revenue with members who pay rent with TenantPay
            between the 7th and 28th - giving you the chance to receive rent savings ranging from
            partial savings to a full month&apos;s rent. It doesn&apos;t get better than that.{' '}
            <a
              role='button'
              tabIndex={0}
              className='hero-h1-gold'
              onClick={() => onNav('rent-drop')}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onNav('rent-drop');
                }
              }}
            >
              See full program terms.
            </a>
          </p>
          <div className='hero-ctas'>
            <button type='button' className='btn-gold' onClick={() => onNav('rewards')}>
              Earn tickets
            </button>
            <button type='button' className='btn-ghost' onClick={() => onNav('rent-drop')}>
              View rent drops
            </button>
          </div>
        </div>
        <div className='hero-r-stack'>
          <div className='hero-r'>
            <div className='tix-block'>
              <div className='tix-top'>
                <div className='tix-lbl'>Drop tickets</div>
              </div>
              <div ref={ticketsRef} className='tix-num' id='tixNum' data-value={tickets}>
                {ticketsPending
                  ? numSkeleton('h-[1em] w-[3.25rem]')
                  : displayedTickets.toLocaleString()}
              </div>
              <div className='tix-sub'>tickets this month</div>
            </div>
            <div>
              <div className='cd-lbl'>Drop closes in</div>
              <div className='cd-row'>
                {COUNTDOWN_PARTS.map((part, index) => (
                  <div key={part.key} style={{ display: 'contents' }}>
                    {index > 0 ? <div className='cdsep'>:</div> : null}
                    <div className='cdbox'>
                      <span
                        className='cdnum'
                        id={
                          part.key === 'days'
                            ? 'cd'
                            : part.key === 'hours'
                              ? 'ch'
                              : part.key === 'minutes'
                                ? 'cm'
                                : 'cs'
                        }
                      >
                        {countdownPending
                          ? '-'
                          : formatCountdownValue(countdown[part.key], part.pad)}
                      </span>
                      <div className='cdsub'>{part.label}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <button type='button' className='hero-pay-btn' onClick={() => onNav('pay')}>
            <PayRentSparkleIcon />
            Pay rent now
          </button>
        </div>
      </div>
    </div>
  );
};
